// TopWallet — Dynamic detail page for each sub-section.
// Route: /topwallet/:seccion (carteras | redes | seguridad | defi)
import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, GitCompare, Clock } from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { getTopWalletI18n } from "../i18n/topwallet";
import { TOPWALLET_SVG_BY_ID } from "../components/topwallet/TopWalletCardSvgs";
import { getArticulosPorSeccion } from "../data/topwalletArticulos";

const NIVEL_STYLES = {
  basico: { border: "rgba(0,230,118,.5)", bg: "rgba(0,230,118,.12)", text: "#00e676" },
  intermedio: { border: "rgba(245,166,35,.5)", bg: "rgba(245,166,35,.12)", text: "#f5a623" },
  avanzado: { border: "rgba(255,51,85,.5)", bg: "rgba(255,51,85,.12)", text: "#ff3355" },
};

const TopWalletSeccionPage = () => {
  const { seccion } = useParams();
  const { lang } = useLanguage();
  const ui = useMemo(() => getTopWalletI18n(lang), [lang]);
  const card = ui.cards.find((c) => c.id === seccion);
  const data = ui.seccion(seccion);

  if (!card || !data) return <Navigate to="/#topwallet" replace />;

  const Illustration = TOPWALLET_SVG_BY_ID[seccion];
  const articulos = getArticulosPorSeccion(seccion, lang);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.22),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_60%,rgba(245,166,35,0.10),transparent_55%)]" />
      </div>

      {/* Header */}
      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to="/#topwallet"
            className="inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="seccion-back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">{ui.breadcrumbRoot}</span>
          </Link>
          <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-4 py-10 md:px-10 md:py-14">
        {/* Breadcrumb mini */}
        <div className="mb-4 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
          <Link to="/#topwallet" className="hover:text-cyan-300">{ui.breadcrumbRoot}</Link>
          <span className="text-white/30">→</span>
          <span className="text-cyan-200">{card.titulo}</span>
        </div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(59,130,246,0.8)]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-200">
              {card.numero} · {card.badge}
            </span>
          </div>
          <h1
            data-testid="seccion-title"
            className="font-display text-3xl font-black tracking-tight md:text-5xl"
          >
            <span className="mr-3">{card.icono}</span>
            {card.titulo}
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/65 md:text-lg">
            {data.descripcion}
          </p>
        </motion.div>

        {/* Illustration banner */}
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] shadow-[0_0_40px_rgba(59,130,246,0.10)]"
        >
          <div className="aspect-[540/340] w-full">{Illustration ? <Illustration /> : null}</div>
        </motion.div>

        {/* Conceptos */}
        <div className="mt-12">
          <h2 className="mb-5 text-xs font-bold uppercase tracking-[0.32em] text-white/45 md:text-sm">
            {ui.conceptosTitle}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            {data.conceptos.map((c, i) => {
              const s = NIVEL_STYLES[c.tipo] || NIVEL_STYLES.basico;
              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: 0.05 * i }}
                  data-testid={`seccion-concepto-${i}`}
                  className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition-colors hover:border-cyan-400/30 md:p-6"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div
                      className="flex h-11 w-11 items-center justify-center rounded-xl text-2xl"
                      style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.25)" }}
                    >
                      {c.icono}
                    </div>
                    <span
                      className="rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.2em]"
                      style={{ border: `1px solid ${s.border}`, background: s.bg, color: s.text }}
                    >
                      {ui.nivelBadge[c.tipo] || c.tipo.toUpperCase()}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold leading-snug text-white md:text-xl">{c.titulo}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/65">{c.descripcion}</p>
                </motion.article>
              );
            })}
          </div>
        </div>

        {/* Artículos en profundidad */}
        {articulos.length > 0 && (
          <div className="mt-14">
            <h2 className="mb-5 text-xs font-bold uppercase tracking-[0.32em] text-white/45 md:text-sm">
              {ui.articulosTitle}
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
              {articulos.map((art, i) => {
                const ns = NIVEL_STYLES[art.nivel] || NIVEL_STYLES.basico;
                return (
                  <motion.article
                    key={art.slug}
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55, delay: 0.05 * i }}
                    data-testid={`articulo-card-${art.slug}`}
                    className="group relative flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition-all hover:border-cyan-400/40 hover:shadow-[0_0_30px_rgba(59,130,246,0.18)] md:p-6"
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div
                        className="flex h-11 w-11 items-center justify-center rounded-xl text-2xl"
                        style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.25)" }}
                      >
                        {art.icono}
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.2em]"
                          style={{ border: `1px solid ${ns.border}`, background: ns.bg, color: ns.text }}
                        >
                          {ui.nivelBadge[art.nivel]}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-white/50">
                          <Clock className="h-3 w-3" />
                          {art.tiempoLectura}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold leading-snug text-white md:text-xl">{art.titulo}</h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-white/60">{art.resumen}</p>
                    <Link
                      to={`/topwallet/${seccion}/${art.slug}`}
                      data-testid={`articulo-cta-${art.slug}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-300 transition-colors hover:text-cyan-200"
                    >
                      {ui.leerArticulo}
                    </Link>
                  </motion.article>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer CTAs */}
        <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/#topwallet"
            data-testid="seccion-cta-back"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white/75 transition-colors hover:border-cyan-400/40 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            {ui.volver}
          </Link>
          <Link
            to="/topwallet/comparador"
            data-testid="seccion-cta-comparador"
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-[#07080f] shadow-[0_4px_16px_rgba(245,166,35,0.3)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_28px_rgba(245,166,35,0.5)]"
            style={{ background: "linear-gradient(135deg, #f5a623, #e8941a)" }}
          >
            <GitCompare className="h-4 w-4" />
            {ui.comparadorCta}
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TopWalletSeccionPage;
